import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Edit2, DollarSign, Calculator, Search, Play, CheckCircle, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AuthorizationDialog } from '@/components/ui/authorization-dialog';
import { useAutoRefresh } from '@/hooks/use-auto-refresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PreProductionCosting() {
  const navigate = useNavigate();
  
  // Design list
  const [designs, setDesigns] = useState([]);
  const [filteredDesigns, setFilteredDesigns] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Selected design for costing
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [costingData, setCostingData] = useState(null);
  const [costingRun, setCostingRun] = useState(false);
  const [viewMode, setViewMode] = useState(false); // true = view only, false = edit mode
  
  // Fabrics data
  const [fabrics, setFabrics] = useState([]);
  
  // Digital Design data
  const [digitalDesign, setDigitalDesign] = useState(null);
  
  // Trims data
  const [trims, setTrims] = useState([]);
  
  // Shipping and Logistics
  const [shippingLogisticsPerMeter, setShippingLogisticsPerMeter] = useState(0.15); // fixed $0.15 per meter
  const [incurredLogisticCosts, setIncurredLogisticCosts] = useState(0.10); // fixed $0.10 for digital design
  
  // CMT data
  const [estProductionTime, setEstProductionTime] = useState(0); // in minutes
  const [sewingRate, setSewingRate] = useState(0.03); // fixed $0.03 per minute
  const [finishFoldingRate, setFinishFoldingRate] = useState(1.00); // fixed $1
  const [overheadMultiplier, setOverheadMultiplier] = useState(0.6); // 60%
  
  // Other Costs
  const [otherCosts, setOtherCosts] = useState([]);
  
  // Authorization
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [pendingValue, setPendingValue] = useState(null);
  const [pendingDeleteCostId, setPendingDeleteCostId] = useState(null);
  
  useEffect(() => {
    fetchApprovedDesigns();
  }, []);
  
  useEffect(() => {
    filterDesigns();
  }, [searchQuery, designs]);
  
  const fetchApprovedDesigns = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/designs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter only APPROVED designs
      const approved = response.data.filter(d => d.status === 'APPROVED');
      
      // LIVE COSTING: Recalculate costs with current master data prices for the grid
      const approvedWithLiveCosts = await Promise.all(approved.map(async (design) => {
        if (design.costing_approved && design.costing_data) {
          // Recalculate with live master data prices
          const liveCost = await calculateLiveCostForGrid(design, token);
          return {
            ...design,
            costing_data: {
              ...design.costing_data,
              preProductionCosting: liveCost.preProductionCosting,
              withMargin: liveCost.withMargin
            }
          };
        }
        return design;
      }));
      
      setDesigns(approvedWithLiveCosts);
      setFilteredDesigns(approvedWithLiveCosts);
    } catch (error) {
      console.error('Error fetching approved designs:', error);
      toast.error('Failed to fetch approved designs');
    }
  };

  // Auto-refresh every 5 seconds, but pause when viewing or calculating
  const isUserEditing = selectedDesign !== null || authDialogOpen;
  useAutoRefresh(fetchApprovedDesigns, 5, isUserEditing);
  
  const calculateLiveCostForGrid = async (design, token) => {
    try {
      let fabricsTotal = 0;
      let digitalDesignTotal = 0;
      let trimsTotal = 0;
      let cmtTotal = 0;
      let otherCostsTotal = 0;
      
      // Calculate fabrics with current prices
      if (design.fabrics && design.fabrics.length > 0) {
        const fabricCosts = await Promise.all(design.fabrics.map(async (fabric) => {
          try {
            const fabricResponse = await axios.get(`${API}/master-data/by-code/FABRIC LN/${fabric.fabric_code}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const colorData = fabricResponse.data.colors?.find(c => c.color_code === fabric.color_code);
            const currentPrice = colorData?.price_per_meter || 0;
            const consumption = parseFloat(fabric.consumption) || 0;
            const shipping = design.costing_data?.shippingLogisticsPerMeter || 0.15;
            return (currentPrice + shipping) * consumption;
          } catch (error) {
            return 0;
          }
        }));
        fabricsTotal = fabricCosts.reduce((sum, cost) => sum + cost, 0);
      }
      
      // Calculate digital design with current price
      if (design.digital_design_code) {
        try {
          const ddResponse = await axios.get(`${API}/master-data/by-code/DIGITAL DESIGN LN-T/${design.digital_design_code}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const colorData = ddResponse.data.colors?.find(c => c.color_code === design.digital_design_colorway);
          const currentPrice = colorData?.price_per_meter || ddResponse.data.price_per_meter || 0;
          const consumption = parseFloat(design.digital_design_consumption) || 0;
          const logistics = design.costing_data?.incurredLogisticCosts || 0.10;
          digitalDesignTotal = (currentPrice + logistics) * consumption;
        } catch (error) {
          digitalDesignTotal = 0;
        }
      }
      
      // Calculate trims with current prices
      if (design.trims && design.trims.length > 0) {
        const trimCosts = await Promise.all(design.trims.map(async (trim) => {
          try {
            const trimResponse = await axios.get(`${API}/master-data/by-code/TRIMS AND ACCESSORIES LN-Z/${trim.trim_code}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const colorData = trimResponse.data.colors?.find(c => c.color_code === trim.color_code);
            const currentPrice = colorData?.price || 0;
            const quantity = parseFloat(trim.quantity) || 0;
            return currentPrice * quantity;
          } catch (error) {
            return 0;
          }
        }));
        trimsTotal = trimCosts.reduce((sum, cost) => sum + cost, 0);
      }
      
      // Calculate CMT (from saved costing data)
      if (design.costing_data) {
        cmtTotal = design.costing_data.cmtTotal || 0;
        otherCostsTotal = design.costing_data.otherCostsTotal || 0;
      }
      
      const preProductionCosting = fabricsTotal + digitalDesignTotal + trimsTotal + cmtTotal + otherCostsTotal;
      const withMargin = preProductionCosting * 1.15;
      
      return { preProductionCosting, withMargin };
    } catch (error) {
      console.error('Error calculating live cost for grid:', error);
      // Return original saved values if calculation fails
      return {
        preProductionCosting: design.costing_data?.preProductionCosting || 0,
        withMargin: design.costing_data?.withMargin || 0
      };
    }
  };
  
  const filterDesigns = () => {
    if (!searchQuery.trim()) {
      setFilteredDesigns(designs);
      return;
    }
    
    const query = searchQuery.toLowerCase().trim();
    
    const filtered = designs.filter(design => {
      // Search by LN-X code
      if (design.article_code?.toLowerCase().includes(query)) {
        return true;
      }
      
      // Search by barcode - if query matches the article code in barcode
      // Barcode format typically contains the LN-X code
      if (design.article_barcode) {
        // Check if the barcode data URL contains the search query
        // or if the design code matches the scanned barcode
        return design.article_code?.toLowerCase() === query;
      }
      
      return false;
    });
    
    setFilteredDesigns(filtered);
    
    // If exact match found by barcode scan, auto-select it
    if (filtered.length === 1 && query.startsWith('ln-x-')) {
      handleViewDesign(filtered[0]);
      setSearchQuery(''); // Clear search after selection
    }
  };
  
  const getCostingStatus = (design) => {
    // Check if costing exists for this design
    return design.costing_approved ? 'COMPLETED' : 'PENDING';
  };
  
  const handleViewDesign = async (design) => {
    setSelectedDesign(design);
    setCostingRun(false);
    setCostingData(null);
    setFabrics([]);
    setDigitalDesign(null);
    setTrims([]);
    setEstProductionTime(0);
    setOtherCosts([]);
    
    // Check if costing already exists
    if (design.costing_approved && design.costing_data) {
      // LIVE COSTING: Re-fetch current master data prices and recalculate
      toast.info('Fetching live pricing data...');
      await recalculateWithLivePrices(design);
      setCostingRun(true);
      setViewMode(true); // Show in view-only mode
    } else {
      setViewMode(false); // PENDING items go straight to edit mode
    }
  };
  
  const recalculateWithLivePrices = async (design) => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch fabrics with CURRENT prices from master data
      const fabricsWithLivePrices = await Promise.all((design.fabrics || []).map(async (fabric) => {
        try {
          const fabricResponse = await axios.get(`${API}/master-data/by-code/FABRIC LN/${fabric.fabric_code}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const colorData = fabricResponse.data.colors?.find(c => c.color_code === fabric.color_code);
          const currentPrice = colorData?.price_per_meter || 0;
          const consumption = parseFloat(fabric.consumption) || 0;
          const shipping = shippingLogisticsPerMeter;
          
          return {
            ...fabric,
            price_per_meter: currentPrice,
            consumption: consumption,
            shipping_logistics_per_meter: shipping,
            total: (currentPrice + shipping) * consumption
          };
        } catch (error) {
          console.error(`Error fetching fabric price for ${fabric.fabric_code}:`, error);
          return {
            ...fabric,
            price_per_meter: 0,
            consumption: parseFloat(fabric.consumption) || 0,
            shipping_logistics_per_meter: shippingLogisticsPerMeter,
            total: 0
          };
        }
      }));
      
      setFabrics(fabricsWithLivePrices);
      
      // Fetch digital design with CURRENT price
      if (design.digital_design_code) {
        try {
          const ddResponse = await axios.get(`${API}/master-data/by-code/DIGITAL DESIGN LN-T/${design.digital_design_code}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const colorData = ddResponse.data.colors?.find(c => c.color_code === design.digital_design_colorway);
          const currentPrice = colorData?.price_per_meter || ddResponse.data.price_per_meter || 0;
          const consumption = parseFloat(design.digital_design_consumption) || 0;
          const logistics = incurredLogisticCosts;
          
          setDigitalDesign({
            code: design.digital_design_code,
            colorway: design.digital_design_colorway || 'N/A',
            price_per_meter: currentPrice,
            consumption: consumption,
            incurred_logistic_costs: logistics,
            total: (currentPrice + logistics) * consumption
          });
        } catch (error) {
          console.error('Error fetching digital design price:', error);
          setDigitalDesign(null);
        }
      }
      
      // Fetch trims with CURRENT prices
      const trimsWithLivePrices = await Promise.all((design.trims || []).map(async (trim) => {
        try {
          const trimResponse = await axios.get(`${API}/master-data/by-code/TRIMS AND ACCESSORIES LN-Z/${trim.trim_code}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const colorData = trimResponse.data.colors?.find(c => c.color_code === trim.color_code);
          const currentPrice = colorData?.price || 0;
          const quantity = parseFloat(trim.quantity) || 0;
          
          return {
            ...trim,
            price: currentPrice,
            quantity: quantity,
            total: currentPrice * quantity
          };
        } catch (error) {
          console.error(`Error fetching trim price for ${trim.trim_code}:`, error);
          return {
            ...trim,
            price: 0,
            quantity: parseFloat(trim.quantity) || 0,
            total: 0
          };
        }
      }));
      
      setTrims(trimsWithLivePrices);
      
      // Fetch production time
      try {
        const samplingResponse = await axios.get(`${API}/sampling-time-tracker/by-article/${design.article_code}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (samplingResponse.data && samplingResponse.data.total_time_seconds) {
          const productionTimeMinutes = Math.floor(samplingResponse.data.total_time_seconds * 0.65 / 60);
          setEstProductionTime(productionTimeMinutes);
        } else {
          setEstProductionTime(0);
        }
      } catch (error) {
        console.error('Error fetching sampling time:', error);
        setEstProductionTime(0);
      }
      
      // Load other costs (these don't have master data, keep as-is)
      if (design.costing_data && design.costing_data.otherCosts) {
        setOtherCosts(design.costing_data.otherCosts);
      }
      
      // Load CMT rates
      if (design.costing_data) {
        setSewingRate(design.costing_data.sewingRate || 0.01);
        setFinishFoldingRate(design.costing_data.finishFoldingRate || 1.00);
        setOverheadMultiplier(design.costing_data.overheadMultiplier || 0.6);
        setShippingLogisticsPerMeter(design.costing_data.shippingLogisticsPerMeter || 0.15);
        setIncurredLogisticCosts(design.costing_data.incurredLogisticCosts || 0.10);
      }
      
      toast.success('Live pricing data loaded');
    } catch (error) {
      console.error('Error recalculating with live prices:', error);
      toast.error('Failed to fetch live pricing data');
    }
  };
  
  const loadExistingCosting = (costingData) => {
    setFabrics(costingData.fabrics || []);
    setDigitalDesign(costingData.digitalDesign || null);
    setTrims(costingData.trims || []);
    setEstProductionTime(costingData.estProductionTime || 0);
    setSewingRate(costingData.sewingRate || 0.01);
    setFinishFoldingRate(costingData.finishFoldingRate || 1.00);
    setOverheadMultiplier(costingData.overheadMultiplier || 0.6);
    setShippingLogisticsPerMeter(costingData.shippingLogisticsPerMeter || 0.15);
    setIncurredLogisticCosts(costingData.incurredLogisticCosts || 0.10);
    setOtherCosts(costingData.otherCosts || []);
    setCostingData(costingData);
  };
  
  const handleRunCosting = async () => {
    if (!selectedDesign) return;
    
    toast.info('Running costing calculations...');
    
    try {
      const token = localStorage.getItem('token');
      
      // Use the selectedDesign directly instead of re-fetching
      const design = selectedDesign;
      
      // Process Fabrics with price fetching
      const fabricsWithPrices = await Promise.all((design.fabrics || []).map(async (fabric) => {
        try {
          const fabricResponse = await axios.get(`${API}/master-data/by-code/FABRIC LN/${fabric.fabric_code}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const colorData = fabricResponse.data.colors?.find(c => c.color_code === fabric.color_code);
          const price = colorData?.price_per_meter || 0;
          const consumption = parseFloat(fabric.consumption) || 0;
          const shippingLogistics = shippingLogisticsPerMeter;
          
          return {
            ...fabric,
            price_per_meter: price,
            consumption: consumption,
            shipping_logistics_per_meter: shippingLogistics,
            total: (price + shippingLogistics) * consumption
          };
        } catch (error) {
          console.error(`Error fetching fabric price for ${fabric.fabric_code}:`, error);
          return {
            ...fabric,
            price_per_meter: 0,
            consumption: parseFloat(fabric.consumption) || 0,
            shipping_logistics_per_meter: shippingLogisticsPerMeter,
            total: 0
          };
        }
      }));
      
      setFabrics(fabricsWithPrices);
      
      // Process Digital Design with price fetching
      if (design.digital_design_code) {
        try {
          const ddResponse = await axios.get(`${API}/master-data/by-code/DIGITAL DESIGN LN-T/${design.digital_design_code}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const colorData = ddResponse.data.colors?.find(c => c.color_code === design.digital_design_colorway);
          const price = colorData?.price_per_meter || ddResponse.data.price_per_meter || 0;
          const consumption = parseFloat(design.digital_design_consumption) || 0;
          const logistics = incurredLogisticCosts;
          
          setDigitalDesign({
            code: design.digital_design_code,
            colorway: design.digital_design_colorway || 'N/A',
            price_per_meter: price,
            consumption: consumption,
            incurred_logistic_costs: logistics,
            total: (price + logistics) * consumption
          });
        } catch (error) {
          console.error('Error fetching digital design price:', error);
          setDigitalDesign({
            code: design.digital_design_code,
            colorway: design.digital_design_colorway || 'N/A',
            price_per_meter: 0,
            consumption: parseFloat(design.digital_design_consumption) || 0,
            incurred_logistic_costs: incurredLogisticCosts,
            total: 0
          });
        }
      } else {
        setDigitalDesign(null);
      }
      
      // Process Trims with price fetching
      const trimsWithPrices = await Promise.all((design.trims || []).map(async (trim) => {
        try {
          const trimResponse = await axios.get(`${API}/master-data/by-code/TRIMS AND ACCESSORIES LN-Z/${trim.trim_code}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const colorData = trimResponse.data.colors?.find(c => c.color_code === trim.color_code);
          const price = colorData?.price || 0;
          const quantity = parseFloat(trim.quantity) || 0;
          
          return {
            ...trim,
            price: price,
            quantity: quantity,
            total: price * quantity
          };
        } catch (error) {
          console.error(`Error fetching trim price for ${trim.trim_code}:`, error);
          return {
            ...trim,
            price: 0,
            quantity: parseFloat(trim.quantity) || 0,
            total: 0
          };
        }
      }));
      
      setTrims(trimsWithPrices);
      
      // Fetch Estimated Production Time from Machine Operator section (Sampling Time Tracker)
      try {
        const samplingResponse = await axios.get(`${API}/sampling-time-tracker/by-article/${design.article_code}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // API returns null if no data found, not an error
        if (samplingResponse.data && samplingResponse.data.total_time_seconds) {
          // Calculate production time same as Fashion Design: total_time_seconds * 0.65 / 60
          const productionTimeMinutes = Math.floor(samplingResponse.data.total_time_seconds * 0.65 / 60);
          setEstProductionTime(productionTimeMinutes);
        } else {
          setEstProductionTime(0);
        }
      } catch (error) {
        console.error('Error fetching sampling time:', error);
        setEstProductionTime(0);
      }
      
      setCostingRun(true);
      toast.success('Costing calculated successfully!');
    } catch (error) {
      console.error('Error running costing:', error);
      toast.error('Failed to calculate costing');
    }
  };
  
  const handleApproveCosting = async () => {
    if (!selectedDesign || !costingRun) {
      toast.error('Please run costing first');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      const costingData = {
        fabrics,
        digitalDesign,
        trims,
        estProductionTime,
        sewingRate,
        finishFoldingRate,
        overheadMultiplier,
        shippingLogisticsPerMeter,
        incurredLogisticCosts,
        otherCosts,
        fabricsTotal: calculateFabricsTotal(),
        digitalDesignTotal: calculateDigitalDesignTotal(),
        trimsTotal: calculateTrimsTotal(),
        cmtTotal: calculateCMTTotal(),
        otherCostsTotal: calculateOtherCostsTotal(),
        preProductionCosting: calculatePreProductionCosting(),
        withMargin: calculateWithMargin()
      };
      
      // Update design with costing data
      await axios.put(`${API}/designs/${selectedDesign.id}`, {
        ...selectedDesign,
        costing_approved: true,
        costing_data: costingData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Costing approved and saved!');
      setSelectedDesign(null);
      fetchApprovedDesigns(); // Refresh list
    } catch (error) {
      console.error('Error approving costing:', error);
      toast.error('Failed to approve costing');
    }
  };
  
  // Edit handlers with authorization
  const requestEdit = (field, currentValue) => {
    setEditingField(field);
    setPendingValue(currentValue);
    setAuthDialogOpen(true);
  };
  
  const handleAuthSuccess = () => {
    // Check if this is a delete operation
    if (pendingDeleteCostId) {
      executeDeleteOtherCost();
      setAuthDialogOpen(false);
      setPendingDeleteCostId(null);
      return;
    }
    
    // Otherwise, it's a field edit operation
    if (editingField && pendingValue !== null) {
      const [section, index, field] = editingField.split('.');
      
      if (section === 'fabric') {
        const updatedFabrics = [...fabrics];
        const fabricIndex = parseInt(index);
        updatedFabrics[fabricIndex][field] = parseFloat(pendingValue) || 0;
        
        if (field === 'price_per_meter' || field === 'consumption' || field === 'shipping_logistics_per_meter') {
          const price = updatedFabrics[fabricIndex].price_per_meter || 0;
          const shipping = updatedFabrics[fabricIndex].shipping_logistics_per_meter || 0;
          const consumption = updatedFabrics[fabricIndex].consumption || 0;
          updatedFabrics[fabricIndex].total = (price + shipping) * consumption;
        }
        
        setFabrics(updatedFabrics);
      } else if (section === 'digitalDesign') {
        const updated = { ...digitalDesign };
        updated[field] = parseFloat(pendingValue) || 0;
        
        if (field === 'price_per_meter' || field === 'consumption' || field === 'incurred_logistic_costs') {
          const price = updated.price_per_meter || 0;
          const logistics = updated.incurred_logistic_costs || 0;
          const consumption = updated.consumption || 0;
          updated.total = (price + logistics) * consumption;
        }
        
        setDigitalDesign(updated);
      } else if (section === 'trim') {
        const updatedTrims = [...trims];
        const trimIndex = parseInt(index);
        updatedTrims[trimIndex][field] = parseFloat(pendingValue) || 0;
        
        if (field === 'price' || field === 'quantity') {
          updatedTrims[trimIndex].total = 
            updatedTrims[trimIndex].price * updatedTrims[trimIndex].quantity;
        }
        
        setTrims(updatedTrims);
      } else if (section === 'cmt') {
        if (field === 'sewingRate') setSewingRate(parseFloat(pendingValue) || 0);
        if (field === 'finishFoldingRate') setFinishFoldingRate(parseFloat(pendingValue) || 0);
        if (field === 'overheadMultiplier') setOverheadMultiplier(parseFloat(pendingValue) || 0);
        if (field === 'estProductionTime') setEstProductionTime(parseFloat(pendingValue) || 0);
      } else if (section === 'shipping') {
        if (field === 'shippingLogisticsPerMeter') {
          setShippingLogisticsPerMeter(parseFloat(pendingValue) || 0);
          // Recalculate all fabric totals with new shipping cost
          const updatedFabrics = fabrics.map(fabric => ({
            ...fabric,
            shipping_logistics_per_meter: parseFloat(pendingValue) || 0,
            total: (fabric.price_per_meter + (parseFloat(pendingValue) || 0)) * fabric.consumption
          }));
          setFabrics(updatedFabrics);
        }
      } else if (section === 'logistics') {
        if (field === 'incurredLogisticCosts') {
          setIncurredLogisticCosts(parseFloat(pendingValue) || 0);
          // Recalculate digital design total with new logistic cost
          if (digitalDesign) {
            const updatedDD = {
              ...digitalDesign,
              incurred_logistic_costs: parseFloat(pendingValue) || 0,
              total: (digitalDesign.price_per_meter + (parseFloat(pendingValue) || 0)) * digitalDesign.consumption
            };
            setDigitalDesign(updatedDD);
          }
        }
      }
    }
    
    setAuthDialogOpen(false);
    setEditingField(null);
    setPendingValue(null);
  };
  
  // Calculations
  const calculateFabricsTotal = () => {
    return fabrics.reduce((sum, f) => sum + (f.total || 0), 0);
  };
  
  const calculateDigitalDesignTotal = () => {
    return digitalDesign ? (digitalDesign.total || 0) : 0;
  };
  
  const calculateTrimsTotal = () => {
    return trims.reduce((sum, t) => sum + (t.total || 0), 0);
  };
  
  const calculateSewingCost = () => {
    // Minutes × $0.01 per minute
    return estProductionTime * sewingRate;
  };
  
  const calculateOverheads = () => {
    // (Sewing Cost + Finish/Folding) × 0.6
    const sewingCost = calculateSewingCost();
    return (sewingCost + finishFoldingRate) * overheadMultiplier;
  };
  
  const calculateCMTTotal = () => {
    // Sewing + Finish/Folding + Overheads
    const sewingCost = calculateSewingCost();
    const overheads = calculateOverheads();
    return sewingCost + finishFoldingRate + overheads;
  };
  
  const calculatePreProductionCosting = () => {
    return calculateFabricsTotal() + calculateDigitalDesignTotal() + calculateTrimsTotal() + calculateCMTTotal() + calculateOtherCostsTotal();
  };
  
  const calculateWithMargin = () => {
    return calculatePreProductionCosting() * 1.15;
  };
  
  const formatTime = (minutes) => {
    return `${Math.floor(minutes)} minutes`;
  };
  
  // Other Costs Management
  const addOtherCost = () => {
    const newCost = {
      id: Date.now(),
      name: '',
      uom: 'Per Piece',
      price: 0,
      fabricIndex: 0, // Index of fabric to use for Per Meter calculation
      total: 0
    };
    setOtherCosts([...otherCosts, newCost]);
  };
  
  const removeOtherCost = (id) => {
    // If costing is approved, require authorization for deletion
    if (selectedDesign.costing_approved) {
      setPendingDeleteCostId(id);
      setAuthDialogOpen(true);
    } else {
      // Not approved yet, delete directly
      setOtherCosts(otherCosts.filter(cost => cost.id !== id));
    }
  };
  
  const executeDeleteOtherCost = () => {
    if (pendingDeleteCostId) {
      setOtherCosts(otherCosts.filter(cost => cost.id !== pendingDeleteCostId));
      setPendingDeleteCostId(null);
    }
  };
  
  const updateOtherCost = (id, field, value) => {
    setOtherCosts(otherCosts.map(cost => {
      if (cost.id === id) {
        const updated = { ...cost, [field]: value };
        
        // Recalculate total
        if (updated.uom === 'Per Piece') {
          updated.total = parseFloat(updated.price) || 0;
        } else if (updated.uom === 'Per Meter') {
          const fabric = fabrics[updated.fabricIndex];
          const consumption = fabric ? (fabric.consumption || 0) : 0;
          updated.total = (parseFloat(updated.price) || 0) * consumption;
        }
        
        return updated;
      }
      return cost;
    }));
  };
  
  const calculateOtherCostsTotal = () => {
    return otherCosts.reduce((sum, cost) => sum + (cost.total || 0), 0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white shadow-md border-b-2 border-black mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                PRE-PRODUCTION COSTING
              </h1>
              <p className="text-sm text-gray-600">Automated costing for approved designs</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {!selectedDesign ? (
          <>
            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Enter LN-X code or scan barcode"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Grid View */}
            {filteredDesigns.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <p className="text-gray-500 text-lg">
                  {designs.length === 0 ? 'No approved designs found' : 'No matching designs found'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredDesigns.map((design) => {
                  const status = getCostingStatus(design);
                  return (
                    <div
                      key={design.id}
                      className="bg-white rounded-lg shadow-lg border-2 border-black p-6 cursor-pointer hover:shadow-xl transition-all"
                      onClick={() => handleViewDesign(design)}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold uppercase tracking-tight">{design.article_code}</h3>
                        <span className={`px-3 py-1 text-xs font-bold ${
                          status === 'COMPLETED' 
                            ? 'bg-green-100 text-black' 
                            : 'bg-yellow-100 text-black'
                        }`}>
                          {status}
                        </span>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Category:</span>
                          <span className="font-bold">{design.category || 'N/A'}</span>
                        </div>
                        {status === 'COMPLETED' && design.costing_data && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Total Cost:</span>
                            <span className="font-bold text-black">
                              ${design.costing_data.withMargin?.toFixed(2) || '0.00'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <>
            {viewMode ? (
              /* ============ VIEW MODE - Read Only Display ============ */
              <div className="space-y-6">
                {/* Header with Edit Button */}
                <div className="bg-white border-4 border-black p-8">
                  <div className="flex items-center justify-between border-b-2 border-black pb-4">
                    <div>
                      <h2 className="text-3xl font-bold uppercase tracking-wider">{selectedDesign.article_code}</h2>
                      <p className="text-sm uppercase text-gray-700 mt-1">{selectedDesign.category}</p>
                      <span className="inline-block mt-2 px-4 py-1 border-2 border-black text-xs font-bold uppercase">
                        COSTING COMPLETED
                      </span>
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={() => setSelectedDesign(null)}
                        variant="outline"
                        className="border-2 border-black bg-white hover:bg-gray-100 text-black"
                      >
                        Back to List
                      </Button>
                      <Button
                        onClick={() => setViewMode(false)}
                        className="bg-black hover:bg-gray-800 text-white border-2 border-black"
                      >
                        <Edit2 className="w-4 h-4 mr-2" />
                        Edit Costing
                      </Button>
                    </div>
                  </div>
                </div>

                {/* View Only Summary - Financial Document Style */}
                <div className="bg-white border-4 border-black">
                  <div className="bg-black text-white p-4">
                    <h2 className="text-xl font-bold uppercase tracking-wider">PRE-PRODUCTION COSTING STATEMENT</h2>
                  </div>
                  
                  <div className="p-8">
                    {/* Cost Breakdown Table */}
                    <table className="w-full border-2 border-black">
                      <thead>
                        <tr className="border-b-2 border-black bg-gray-100">
                          <th className="text-left p-4 border-r-2 border-black font-bold uppercase text-sm">COST CATEGORY</th>
                          <th className="text-right p-4 font-bold uppercase text-sm">AMOUNT (USD)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-black">
                          <td className="p-4 border-r-2 border-black font-medium">Fabrics Total</td>
                          <td className="p-4 text-right font-mono">${selectedDesign.costing_data?.fabricsTotal?.toFixed(2) || '0.00'}</td>
                        </tr>
                        {selectedDesign.costing_data?.digitalDesign && (
                          <tr className="border-b border-black">
                            <td className="p-4 border-r-2 border-black font-medium">Digital Design Total</td>
                            <td className="p-4 text-right font-mono">${selectedDesign.costing_data?.digitalDesignTotal?.toFixed(2) || '0.00'}</td>
                          </tr>
                        )}
                        <tr className="border-b border-black">
                          <td className="p-4 border-r-2 border-black font-medium">Trims & Accessories Total</td>
                          <td className="p-4 text-right font-mono">${selectedDesign.costing_data?.trimsTotal?.toFixed(2) || '0.00'}</td>
                        </tr>
                        <tr className="border-b border-black">
                          <td className="p-4 border-r-2 border-black font-medium">CMT (Cut, Make, Trim) Total</td>
                          <td className="p-4 text-right font-mono">${selectedDesign.costing_data?.cmtTotal?.toFixed(2) || '0.00'}</td>
                        </tr>
                        {selectedDesign.costing_data?.otherCosts?.length > 0 && (
                          <tr className="border-b border-black">
                            <td className="p-4 border-r-2 border-black font-medium">Other Costs Total</td>
                            <td className="p-4 text-right font-mono">${selectedDesign.costing_data?.otherCostsTotal?.toFixed(2) || '0.00'}</td>
                          </tr>
                        )}
                        <tr className="border-t-4 border-black bg-gray-100">
                          <td className="p-4 border-r-2 border-black font-bold uppercase">SUBTOTAL (PRE-PRODUCTION COSTING)</td>
                          <td className="p-4 text-right font-bold font-mono">${selectedDesign.costing_data?.preProductionCosting?.toFixed(2) || '0.00'}</td>
                        </tr>
                        <tr className="border-t-2 border-black">
                          <td className="p-4 border-r-2 border-black font-medium">Margin of Error (15%)</td>
                          <td className="p-4 text-right font-mono">${((selectedDesign.costing_data?.preProductionCosting || 0) * 0.15).toFixed(2)}</td>
                        </tr>
                        <tr className="border-t-4 border-double border-black bg-black text-white">
                          <td className="p-4 border-r-2 border-white font-bold uppercase">TOTAL WITH MARGIN</td>
                          <td className="p-4 text-right font-bold font-mono">${selectedDesign.costing_data?.withMargin?.toFixed(2) || '0.00'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              /* ============ EDIT MODE - Full Costing Form ============ */
              <div className="space-y-6">
                {/* Header with Run Costing Button */}
                <div className="bg-white rounded-lg shadow-lg border-2 border-black p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold uppercase tracking-tight">{selectedDesign.article_code}</h2>
                      <p className="text-sm text-gray-600">{selectedDesign.category}</p>
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={() => {
                          if (selectedDesign.costing_approved) {
                            setViewMode(true); // Go back to view mode if already approved
                          } else {
                            setSelectedDesign(null); // Go back to list if pending
                          }
                        }}
                        variant="outline"
                        className="border-2 border-black"
                      >
                        {selectedDesign.costing_approved ? 'Back to View' : 'Back to List'}
                      </Button>
                      {!costingRun && (
                        <Button
                          onClick={handleRunCosting}
                          className="bg-black hover:bg-gray-800 text-white border-2 border-black"
                        >
                          <Play className="w-4 h-4 mr-2" />
                          Run Costing
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {costingRun && (
                <>
                  {/* Fabrics Section */}
                  <div className="bg-white border-4 border-black">
                    <div className="bg-black text-white p-4 border-b-4 border-black">
                      <h2 className="text-lg font-bold uppercase tracking-wider">SECTION A: FABRIC COSTS</h2>
                    </div>
                    <div className="p-6">
                    {fabrics.length > 0 ? (
                      <div className="space-y-4">
                        {fabrics.map((fabric, index) => (
                          <div key={index} className="border-2 border-gray-300 rounded p-4 space-y-3">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label className="text-xs font-bold">FABRIC CODE</Label>
                                <p className="text-sm">{fabric.fabric_code}</p>
                              </div>
                              <div>
                                <Label className="text-xs font-bold">COLOR CODE</Label>
                                <p className="text-sm">{fabric.color_code}</p>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-5 gap-4">
                              <div>
                                <Label className="text-xs font-bold">CONSUMPTION (METERS)</Label>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={fabric.consumption}
                                    readOnly
                                    className="bg-gray-50"
                                  />
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => requestEdit(`fabric.${index}.consumption`, fabric.consumption)}
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                              
                              <div>
                                <Label className="text-xs font-bold">FABRIC PRICE ($)</Label>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={fabric.price_per_meter}
                                    readOnly
                                    className="bg-gray-50"
                                  />
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => requestEdit(`fabric.${index}.price_per_meter`, fabric.price_per_meter)}
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                              
                              <div>
                                <Label className="text-xs font-bold">SHIPPING/LOGISTICS ($)</Label>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={fabric.shipping_logistics_per_meter || shippingLogisticsPerMeter}
                                    readOnly
                                    className="bg-gray-50"
                                  />
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => requestEdit(`shipping.0.shippingLogisticsPerMeter`, shippingLogisticsPerMeter)}
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                              
                              <div className="col-span-2">
                                <Label className="text-xs font-bold">TOTAL COST ($)</Label>
                                <p className="text-sm text-gray-600 mb-1">
                                  ({fabric.price_per_meter?.toFixed(2) || '0.00'} + {(fabric.shipping_logistics_per_meter || shippingLogisticsPerMeter).toFixed(2)}) × {fabric.consumption?.toFixed(2) || '0.00'}
                                </p>
                                <p className="text-lg font-bold text-black">${fabric.total.toFixed(2)}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        <div className="bg-gray-100 border-t-4 border-black p-4 mt-4">
                          <div className="flex justify-between items-center">
                            <span className="text-base font-bold uppercase">TOTAL FABRIC COST</span>
                            <span className="text-base font-bold font-mono">${calculateFabricsTotal().toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-500 p-4">No fabrics in this design</p>
                    )}
                    </div>
                  </div>

                  {/* Digital Design Section */}
                  {digitalDesign && (
                    <div className="bg-white border-4 border-black">
                      <div className="bg-black text-white p-4 border-b-4 border-black">
                        <h2 className="text-lg font-bold uppercase tracking-wider">SECTION B: DIGITAL DESIGN COSTS</h2>
                      </div>
                      <div className="p-6">
                      <div className="border-2 border-gray-300 rounded p-4 space-y-3">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs font-bold">DESIGN CODE</Label>
                            <p className="text-sm">{digitalDesign.code}</p>
                          </div>
                          <div>
                            <Label className="text-xs font-bold">COLORWAY</Label>
                            <p className="text-sm">{digitalDesign.colorway}</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-5 gap-4">
                          <div>
                            <Label className="text-xs font-bold">CONSUMPTION (METERS)</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                step="0.01"
                                value={digitalDesign.consumption}
                                readOnly
                                className="bg-gray-50"
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => requestEdit(`digitalDesign.0.consumption`, digitalDesign.consumption)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          
                          <div>
                            <Label className="text-xs font-bold">DESIGN PRICE ($)</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                step="0.01"
                                value={digitalDesign.price_per_meter}
                                readOnly
                                className="bg-gray-50"
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => requestEdit(`digitalDesign.0.price_per_meter`, digitalDesign.price_per_meter)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          
                          <div>
                            <Label className="text-xs font-bold">LOGISTIC COST ($)</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                step="0.01"
                                value={digitalDesign.incurred_logistic_costs || incurredLogisticCosts}
                                readOnly
                                className="bg-gray-50"
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => requestEdit(`logistics.0.incurredLogisticCosts`, incurredLogisticCosts)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          
                          <div className="col-span-2">
                            <Label className="text-xs font-bold">TOTAL COST ($)</Label>
                            <p className="text-sm text-gray-600 mb-1">
                              ({digitalDesign.price_per_meter?.toFixed(2) || '0.00'} + {(digitalDesign.incurred_logistic_costs || incurredLogisticCosts).toFixed(2)}) × {digitalDesign.consumption?.toFixed(2) || '0.00'}
                            </p>
                            <p className="text-lg font-bold text-black">${digitalDesign.total.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-gray-100 border-t-4 border-black p-4 mt-4">
                        <div className="flex justify-between items-center">
                          <span className="text-base font-bold uppercase">TOTAL DIGITAL DESIGN COST</span>
                          <span className="text-base font-bold font-mono">${calculateDigitalDesignTotal().toFixed(2)}</span>
                        </div>
                      </div>
                      </div>
                    </div>
                  )}

                  {/* Trims & Accessories Section */}
                  <div className="bg-white border-4 border-black">
                    <div className="bg-black text-white p-4 border-b-4 border-black">
                      <h2 className="text-lg font-bold uppercase tracking-wider">SECTION C: TRIMS & ACCESSORIES COSTS</h2>
                    </div>
                    <div className="p-6">
                    {trims.length > 0 ? (
                      <div className="space-y-4">
                        {trims.map((trim, index) => (
                          <div key={index} className="border-2 border-gray-300 rounded p-4 space-y-3">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label className="text-xs font-bold">TRIM CODE</Label>
                                <p className="text-sm">{trim.trim_code}</p>
                              </div>
                              <div>
                                <Label className="text-xs font-bold">COLOR CODE</Label>
                                <p className="text-sm">{trim.color_code}</p>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-4 gap-4">
                              <div>
                                <Label className="text-xs font-bold">QUANTITY</Label>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={trim.quantity}
                                    readOnly
                                    className="bg-gray-50"
                                  />
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => requestEdit(`trim.${index}.quantity`, trim.quantity)}
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                              
                              <div>
                                <Label className="text-xs font-bold">PRICE ($)</Label>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={trim.price}
                                    readOnly
                                    className="bg-gray-50"
                                  />
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => requestEdit(`trim.${index}.price`, trim.price)}
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                              
                              <div className="col-span-2">
                                <Label className="text-xs font-bold">TOTAL COST ($)</Label>
                                <p className="text-lg font-bold text-black">${trim.total.toFixed(2)}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        <div className="bg-gray-100 border-t-4 border-black p-4 mt-4">
                          <div className="flex justify-between items-center">
                            <span className="text-base font-bold uppercase">TOTAL TRIMS & ACCESSORIES COST</span>
                            <span className="text-base font-bold font-mono">${calculateTrimsTotal().toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-500 p-4">No trims in this design</p>
                    )}
                    </div>
                  </div>

                  {/* CMT Section */}
                  <div className="bg-white border-4 border-black">
                    <div className="bg-black text-white p-4 border-b-4 border-black">
                      <h2 className="text-lg font-bold uppercase tracking-wider">
                        <Calculator className="inline w-5 h-5 mr-2" />
                        SECTION D: CMT COSTS (CUT, MAKE, TRIM)
                      </h2>
                    </div>
                    <div className="p-6">
                    
                    <div className="space-y-4">
                      <div className="border-2 border-gray-300 rounded p-4">
                        <Label className="text-xs font-bold mb-2 block">EST. PRODUCTION TIME (MINUTES)</Label>
                        <div className="flex items-center gap-4">
                          <p className="text-lg font-bold">{formatTime(estProductionTime)}</p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => requestEdit(`cmt.0.estProductionTime`, estProductionTime)}
                          >
                            <Edit2 className="w-4 h-4 mr-1" /> Edit Minutes
                          </Button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4">
                        <div className="border-2 border-gray-300 rounded p-4">
                          <Label className="text-xs font-bold mb-2 block">SEWING RATE ($/MINUTE)</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              step="0.01"
                              value={sewingRate}
                              readOnly
                              className="bg-gray-50"
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => requestEdit(`cmt.0.sewingRate`, sewingRate)}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <p className="text-sm text-gray-600 mt-2">
                            Sewing Cost: <span className="font-bold">${calculateSewingCost().toFixed(2)}</span>
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {Math.floor(estProductionTime)} min × ${sewingRate.toFixed(2)}
                          </p>
                        </div>
                        
                        <div className="border-2 border-gray-300 rounded p-4">
                          <Label className="text-xs font-bold mb-2 block">FINISH/FOLDING ($)</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              step="0.01"
                              value={finishFoldingRate}
                              readOnly
                              className="bg-gray-50"
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => requestEdit(`cmt.0.finishFoldingRate`, finishFoldingRate)}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <p className="text-sm text-gray-600 mt-2">Fixed rate</p>
                        </div>
                        
                        <div className="border-2 border-gray-300 rounded p-4">
                          <Label className="text-xs font-bold mb-2 block">OVERHEAD MULTIPLIER</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              step="0.01"
                              value={overheadMultiplier}
                              readOnly
                              className="bg-gray-50"
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => requestEdit(`cmt.0.overheadMultiplier`, overheadMultiplier)}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <p className="text-sm text-gray-600 mt-2">
                            Overheads: <span className="font-bold">${calculateOverheads().toFixed(2)}</span>
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            (${calculateSewingCost().toFixed(2)} + ${finishFoldingRate.toFixed(2)}) × {overheadMultiplier}
                          </p>
                        </div>
                      </div>
                      
                      <div className="bg-gray-100 border-t-4 border-black p-4 mt-4">
                        <div className="flex justify-between items-center">
                          <span className="text-base font-bold uppercase">TOTAL CMT COST</span>
                          <span className="text-base font-bold font-mono">${calculateCMTTotal().toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                    </div>
                  </div>

                  {/* Other Costs Section */}
                  <div className="bg-white border-4 border-black">
                    <div className="bg-black text-white p-4 border-b-4 border-black flex justify-between items-center">
                      <h2 className="text-lg font-bold uppercase tracking-wider">SECTION E: OTHER COSTS</h2>
                      <Button
                        onClick={addOtherCost}
                        className="bg-white hover:bg-gray-200 text-black border-2 border-white"
                        size="sm"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add Cost
                      </Button>
                    </div>
                    <div className="p-6">
                    
                    {otherCosts.length > 0 ? (
                      <div className="space-y-4">
                        {otherCosts.map((cost) => (
                          <div key={cost.id} className="border-2 border-gray-300 rounded p-4 space-y-3">
                            <div className="grid grid-cols-6 gap-4">
                              <div className="col-span-2">
                                <Label className="text-xs font-bold">COST NAME</Label>
                                <Input
                                  type="text"
                                  value={cost.name}
                                  onChange={(e) => updateOtherCost(cost.id, 'name', e.target.value)}
                                  placeholder="Enter cost name"
                                />
                              </div>
                              
                              <div>
                                <Label className="text-xs font-bold">UOM</Label>
                                <select
                                  value={cost.uom}
                                  onChange={(e) => updateOtherCost(cost.id, 'uom', e.target.value)}
                                  className="w-full px-3 py-2 border rounded-md"
                                >
                                  <option value="Per Piece">Per Piece</option>
                                  <option value="Per Meter">Per Meter</option>
                                </select>
                              </div>
                              
                              {cost.uom === 'Per Meter' && fabrics.length > 1 && (
                                <div>
                                  <Label className="text-xs font-bold">FABRIC</Label>
                                  <select
                                    value={cost.fabricIndex}
                                    onChange={(e) => updateOtherCost(cost.id, 'fabricIndex', parseInt(e.target.value))}
                                    className="w-full px-3 py-2 border rounded-md"
                                  >
                                    {fabrics.map((fabric, idx) => (
                                      <option key={idx} value={idx}>
                                        {fabric.fabric_code}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              )}
                              
                              <div>
                                <Label className="text-xs font-bold">PRICE ($)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={cost.price}
                                  onChange={(e) => updateOtherCost(cost.id, 'price', e.target.value)}
                                  placeholder="0.00"
                                />
                              </div>
                              
                              <div>
                                <Label className="text-xs font-bold">TOTAL ($)</Label>
                                <p className="text-lg font-bold text-black mt-2">${cost.total.toFixed(2)}</p>
                                {cost.uom === 'Per Meter' && (
                                  <p className="text-xs text-gray-500">
                                    {cost.price} × {fabrics[cost.fabricIndex]?.consumption.toFixed(2) || 0}m
                                  </p>
                                )}
                              </div>
                              
                              <div className="flex items-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => removeOtherCost(cost.id)}
                                  className="border-2 border-black bg-white hover:bg-gray-100 text-black"
                                >
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        <div className="bg-gray-100 border-t-4 border-black p-4 mt-4">
                          <div className="flex justify-between items-center">
                            <span className="text-base font-bold uppercase">TOTAL OTHER COSTS</span>
                            <span className="text-base font-bold font-mono">${calculateOtherCostsTotal().toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">No other costs added</p>
                    )}
                    </div>
                  </div>

                  {/* Final Costing Summary */}
                  <div className="bg-white border-4 border-black">
                    <div className="bg-black text-white p-4 border-b-4 border-black">
                      <h2 className="text-lg font-bold uppercase tracking-wider">FINAL COSTING STATEMENT</h2>
                    </div>
                    
                    <div className="p-8">
                      {/* Summary Table */}
                      <table className="w-full border-2 border-black mb-6">
                        <thead>
                          <tr className="border-b-2 border-black bg-gray-100">
                            <th className="text-left p-4 border-r-2 border-black font-bold uppercase text-sm">COST CATEGORY</th>
                            <th className="text-right p-4 font-bold uppercase text-sm">AMOUNT (USD)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-black">
                            <td className="p-4 border-r-2 border-black font-medium">Fabrics Total</td>
                            <td className="p-4 text-right font-mono">${calculateFabricsTotal().toFixed(2)}</td>
                          </tr>
                          {digitalDesign && (
                            <tr className="border-b border-black">
                              <td className="p-4 border-r-2 border-black font-medium">Digital Design Total</td>
                              <td className="p-4 text-right font-mono">${calculateDigitalDesignTotal().toFixed(2)}</td>
                            </tr>
                          )}
                          <tr className="border-b border-black">
                            <td className="p-4 border-r-2 border-black font-medium">Trims & Accessories Total</td>
                            <td className="p-4 text-right font-mono">${calculateTrimsTotal().toFixed(2)}</td>
                          </tr>
                          <tr className="border-b border-black">
                            <td className="p-4 border-r-2 border-black font-medium">CMT Total</td>
                            <td className="p-4 text-right font-mono">${calculateCMTTotal().toFixed(2)}</td>
                          </tr>
                          {otherCosts.length > 0 && (
                            <tr className="border-b border-black">
                              <td className="p-4 border-r-2 border-black font-medium">Other Costs Total</td>
                              <td className="p-4 text-right font-mono">${calculateOtherCostsTotal().toFixed(2)}</td>
                            </tr>
                          )}
                          <tr className="border-t-4 border-black bg-gray-100">
                            <td className="p-4 border-r-2 border-black font-bold uppercase">SUBTOTAL (PRE-PRODUCTION COSTING)</td>
                            <td className="p-4 text-right font-bold font-mono">${calculatePreProductionCosting().toFixed(2)}</td>
                          </tr>
                          <tr className="border-t-2 border-black">
                            <td className="p-4 border-r-2 border-black font-medium">Margin of Error (15%)</td>
                            <td className="p-4 text-right font-mono">${(calculatePreProductionCosting() * 0.15).toFixed(2)}</td>
                          </tr>
                          <tr className="border-t-4 border-double border-black bg-black text-white">
                            <td className="p-4 border-r-2 border-white font-bold uppercase">TOTAL WITH MARGIN</td>
                            <td className="p-4 text-right font-bold font-mono">${calculateWithMargin().toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>
                      
                      {/* Approve Costing Button */}
                      <Button
                        onClick={handleApproveCosting}
                        className="w-full bg-black hover:bg-gray-800 text-white text-lg py-6 border-2 border-black"
                      >
                        <CheckCircle className="w-6 h-6 mr-2" />
                        Approve & Finalize Costing
                      </Button>
                    </div>
                  </div>
                </>
              )}
              </div>
            )}
          </>
        )}
      </main>

      {/* Authorization Dialog */}
      <AuthorizationDialog
        open={authDialogOpen}
        onOpenChange={(open) => {
          setAuthDialogOpen(open);
          if (!open) {
            // Clear pending states when dialog closes
            setPendingDeleteCostId(null);
            setEditingField(null);
            setPendingValue(null);
          }
        }}
        onAuthorized={handleAuthSuccess}
        title={pendingDeleteCostId ? "Delete Other Cost" : "Edit Costing Field"}
        description={pendingDeleteCostId ? "Enter authorization code to delete this cost item" : "Enter authorization code to edit this field"}
        correctCode={pendingDeleteCostId ? "1436" : "editcosting"}
        promptMessage={pendingDeleteCostId ? null : "Please enter the new value before authorization:"}
        onPromptValueChange={setPendingValue}
        promptValue={pendingValue}
        promptType="number"
      />
    </div>
  );
}
